import React from 'react';
import styled from 'styled-components';
import AddToCalendarHOC from 'react-add-to-calendar-hoc';
import { differenceInHours, formatISO, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';

import GoogleCalendar from '../images/googlecalendar.inline.svg';

const StyledList = styled.ul`
  padding: 0;
  > a {
    display: block;
    color: white;
    padding: ${({ theme }) => theme.sizes.lvl0} 0;
  }
`;

const StyledButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => theme.sizes.lvl5};
  height: ${({ theme }) => theme.sizes.lvl5};
  border-radius: ${({ theme }) => theme.sizes.lvl5};
  background-color: ${({ theme }) => theme.colours.backgroundPaper};
  cursor: pointer;
  outline: none;
  border: none;
  margin: ${({ theme }) => theme.sizes.lvl2} auto 0;

  ${({ theme }) => theme.breakpoint('tabletPortrait')`
    width: ${theme.sizes.lvl10};
    height: ${theme.sizes.lvl10};
  `}

  &:hover .innerIcon {
    transition: all 0.3s;
    width: ${({ theme }) => theme.sizes.sizeByFactor(3.5)};
    height: ${({ theme }) => theme.sizes.sizeByFactor(3.5)};
  }
`;
const CalendarIcon = styled(GoogleCalendar)`
  width: ${({ theme }) => theme.sizes.lvl3};
`;

const CardLink = styled.div`
  cursor: pointer;
  text-decoration: underline;
  color: ${({ theme }) => theme.colours.white};
  text-align: center;
  overflow-wrap: break-word;
  font-family: ${({ theme }) => theme.font.families.title} !important;
  font-size: ${({ theme }) => theme.font.sizes.md};
  ${({ theme }) => theme.breakpoint('medium')`
    font-size: ${theme.font.sizes.xxl};
  `}
`;

const Button = ({ className, onClick }) => {
  return (
    <>
      <CardLink onClick={onClick}>Add Day 1 to your calender</CardLink>
      <StyledButton className={className} onClick={onClick}>
        <CalendarIcon className="innerIcon" />
      </StyledButton>
    </>
  );
};

Button.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

Button.defaultProps = {
  children: null,
  className: '',
};

const List = ({ children, className }) => {
  return <StyledList className={className}>{children}</StyledList>;
};

List.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

List.defaultProps = {
  children: null,
  className: '',
};

const CalendarButton = ({ className }) => {
  const { site = {} } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            conferenceInformation {
              startTime {
                year
                month
                day
                hour
              }
              endTime {
                year
                month
                day
                hour
              }
            }
          }
        }
      }
    `
  );

  const AddToCalendarButton = AddToCalendarHOC(Button, List);

  const { startTime, endTime } = site?.siteMetadata?.conferenceInformation;
  const startTimeFormatted = formatISO(
    new Date(startTime.year, startTime.month, startTime.day, startTime.hour),
    { format: 'basic' }
  );
  const endTimeFormatted = formatISO(
    new Date(endTime.year, endTime.month, endTime.day, endTime.hour),
    { format: 'basic' }
  );
  const duration = differenceInHours(
    parseISO(endTimeFormatted),
    parseISO(startTimeFormatted)
  );
  const eventInDifferentTimezone = {
    description: site?.siteMetadata?.description,
    duration,
    endDatetime: endTimeFormatted,
    location: 'London',
    startDatetime: startTimeFormatted,
    timezone: 'Europe/London',
    title: site?.siteMetadata?.title,
  };

  return (
    <AddToCalendarButton
      className={className}
      event={eventInDifferentTimezone}
    />
  );
};

CalendarButton.propTypes = {
  className: PropTypes.string,
};

CalendarButton.defaultProps = {
  className: '',
};

export default CalendarButton;
